import React, { useMemo, memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { Copy, Check } from 'lucide-react';
import { Highlight } from 'prism-react-renderer';

import 'katex/dist/katex.min.css';
import styles from './styles/ReactMarkdownMessage.module.css';

// Helper function to preprocess escaped dollar signs
// This prevents currency symbols from being interpreted as math delimiters
const preprocessText = text => {
    if (typeof text !== 'string') return text;

    // Replace escaped dollar signs \$ with a temporary placeholder
    // that won't be interpreted as a math delimiter
    return text.replace(/\\\$/g, '&#36;');
};

const ReactMarkdownMessage = ({
    children,
    copiedCode,
    onCopyCode = () => {},
    setToastMessage = () => {},
}) => {
    // Preprocess the content to handle escaped dollar signs
    const processedContent = useMemo(() => {
        return preprocessText(children);
    }, [children]);

    return (
        <div className={styles.messageMarkdown}>
            <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkMath]}
                rehypePlugins={[rehypeKatex]}
                components={{
                    a({node, children, href, ...props}) {
                        return <a 
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            {...props}
                        >
                            {children}
                        </a>;
                    },
                    code({node, inline, className, children, ...props}) {
                        const match = /language-(\w+)/.exec(className || '');
                        const code = String(children).replace(/\n$/, '');
                        const isInPre = node.position?.start.line !== node.position?.end.line;

                        if ((inline || code.split('\n').length === 1) && !isInPre) {
                            return <code
                                {...props}
                                className={styles.inlineCode}
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                    onCopyCode(code);
                                    setToastMessage({
                                        message: 'Copied to clipboard!',
                                        type: 'info',
                                    });
                                }}
                            >
                                {children}
                            </code>;
                        }

                        return (
                            <div className={styles.codeBlock}>
                                <div className={styles.codeHeader}>
                                    <span>{match ? match[1] : 'text'}</span>
                                    <button
                                        className={styles.copyButton}
                                        onClick={() => onCopyCode(code)}
                                        aria-label="Copy code"
                                    >
                                        {copiedCode === code ? (
                                            <div className={styles.copyBlock}>
                                                <Check size={14} />
                                                <span className={styles.copyBlockText}>Copied!</span>
                                            </div>
                                        ) : (
                                            <div className={styles.copyBlock}>
                                                <Copy size={14} />
                                                <span className={styles.copyBlockText}>Copy code</span>
                                            </div>
                                        )}
                                    </button>
                                </div>
                                
                                <Highlight code={code} language={match ? match[1] : 'text'}>
                                    {({className, style, tokens, getLineProps, getTokenProps}) => (
                                        <pre className={`${styles.pre} ${className}`} style={style}>
                                            {tokens.map((line, i) => (
                                                <div key={i} {...getLineProps({line})}>
                                                    {line.map((token, key) => (
                                                        <span key={key} {...getTokenProps({token})} />
                                                    ))}
                                                </div>
                                            ))}
                                        </pre>
                                    )}
                                </Highlight>
                            </div>
                        );
                    }
                }}
            >
                {processedContent}
            </ReactMarkdown>
        </div>
    );
};

export default memo(ReactMarkdownMessage);
